import React, { ReactNode, useEffect, useRef, useState } from 'react';
import * as Popover from '@radix-ui/react-popover';
import cn from 'classnames';
import styles from './Menus.module.scss';
import { ExpandingPopoverMenu } from './ExpandingMenu';
import { useMountedEffect } from '@support/hooks/use-mounted-effect';
import { useIsOnScreen } from '@support/hooks/use-is-on-screen';

export default function PopoverMenu({
  trigger,
  menu,
  onOpen,
  onClose,
  menuClassName,
  disabled,
  defaultIsOpen = false,
  isOpen: controlledIsOpen,
  setIsOpen: controlledSetIsOpen,
  onMenuKeyDown,
  side = 'auto',
}: {
  trigger: ReactNode;
  menu: ReactNode;
  onOpen?: () => void;
  onClose?: () => void;
  menuClassName?: string;
  disabled?: boolean;
  defaultIsOpen?: boolean;
  isOpen?: boolean;
  setIsOpen?: (isOpen: boolean) => void;
  onMenuKeyDown?: (e: React.KeyboardEvent) => void;
  side?: 'top' | 'bottom' | 'auto';
}) {
  const isMounted = useRef(false);
  const triggerRef = useRef(null);
  const [_isOpen, _setIsOpen] = useState(defaultIsOpen);
  const isOpen = controlledIsOpen !== undefined ? controlledIsOpen : _isOpen;
  const setIsOpen = controlledSetIsOpen || _setIsOpen;

  const shouldOpenBelow = useIsOnScreen(triggerRef, { rootMargin: '0px 0px -280px 0px' });
  let expandFrom = 'bottom-left';
  let _side = 'top';

  if (side === 'bottom' || (shouldOpenBelow && side === 'auto')) {
    expandFrom = 'top-left';
    _side = 'bottom';
  }

  const onOpenChange = (open: boolean) => {
    if (isMounted.current) {
      setIsOpen(open);
    }
  };

  useEffect(() => {
    if (isOpen && onOpen) {
      onOpen();
    }
  }, [isOpen]);

  useMountedEffect(() => {
    if (!isOpen && onClose) {
      onClose();
    }
  }, [isOpen]);

  useEffect(() => {
    setTimeout(() => {
      isMounted.current = true;
    }, 1);

    return () => {
      isMounted.current = false;
    };
  }, []);

  return (
    <Popover.Root open={isOpen} onOpenChange={onOpenChange}>
      <Popover.Trigger
        ref={triggerRef}
        className={cn(styles.popoverTrigger, 'popover-menu-trigger')}
        disabled={disabled}
      >
        {trigger}
      </Popover.Trigger>
      <ExpandingPopoverMenu
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        // @ts-ignore
        expandFrom={expandFrom}
        // @ts-ignore
        side={_side}
        menuClassName={menuClassName}
        onKeyDown={onMenuKeyDown}
      >
        {menu}
      </ExpandingPopoverMenu>
    </Popover.Root>
  );
}
